import { z } from 'zod';
import { DateOnlySchema } from './common.js';
import { AccessLevelSchema, AssignmentAccessLevelSchema } from './records.js';

/**
 * Access reviews (re-certification of software/hardware grants): a review
 * snapshots the live assignments of a collection into items, each decided
 * confirmed/modified/revoked, plus free-text rows for systems that are not
 * per-person grants. Tables access_reviews / access_review_items /
 * access_review_systems (migration 0017); routes in
 * apps/worker/src/routes/access-reviews.ts. Never called a "campaign".
 */

export const ACCESS_REVIEW_STATUSES = ['in_progress', 'completed', 'approved'] as const;
export const AccessReviewStatusSchema = z.enum(ACCESS_REVIEW_STATUSES);
export type AccessReviewStatus = z.infer<typeof AccessReviewStatusSchema>;
export const ACCESS_REVIEW_STATUS_LABELS: Record<AccessReviewStatus, string> = {
  in_progress: 'In progress',
  completed: 'Completed',
  approved: 'Approved',
};

export const ACCESS_REVIEW_DECISIONS = ['pending', 'confirmed', 'modified', 'revoked'] as const;
export const AccessReviewDecisionSchema = z.enum(ACCESS_REVIEW_DECISIONS);
export type AccessReviewDecision = z.infer<typeof AccessReviewDecisionSchema>;
export const ACCESS_REVIEW_DECISION_LABELS: Record<AccessReviewDecision, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  modified: 'Modified',
  revoked: 'Revoked',
};

export const ACCESS_REVIEW_OUTCOMES = ['compliant', 'compliant_actions_open', 'nonconformity'] as const;
export const AccessReviewOutcomeSchema = z.enum(ACCESS_REVIEW_OUTCOMES);
export type AccessReviewOutcome = z.infer<typeof AccessReviewOutcomeSchema>;
export const ACCESS_REVIEW_OUTCOME_LABELS: Record<AccessReviewOutcome, string> = {
  compliant: 'Compliant',
  compliant_actions_open: 'Compliant – action(s) open',
  nonconformity: 'Nonconformity raised',
};

/** Collections whose assignments can be reviewed. */
export const ACCESS_REVIEW_COLLECTIONS = ['software', 'hardware'] as const;
export const AccessReviewCollectionSchema = z.enum(ACCESS_REVIEW_COLLECTIONS);
export type AccessReviewCollection = z.infer<typeof AccessReviewCollectionSchema>;

/** A system reviewed that is not a per-person grant (shared mailboxes, client environments, physical access…). */
export const AccessReviewSystemSchema = z.object({
  id: z.string(),
  reviewId: z.string(),
  system: z.string(),
  accountsReviewed: z.string(),
  privilegedRoles: z.string(),
  changesMade: z.string(),
  outcome: AccessReviewOutcomeSchema,
  sortOrder: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AccessReviewSystem = z.infer<typeof AccessReviewSystemSchema>;

export const UpsertAccessReviewSystemInputSchema = z.object({
  system: z.string().min(1).max(200),
  accountsReviewed: z.string().max(2000).optional(),
  privilegedRoles: z.string().max(2000).optional(),
  changesMade: z.string().max(2000).optional(),
  outcome: AccessReviewOutcomeSchema.optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export type UpsertAccessReviewSystemInput = z.infer<typeof UpsertAccessReviewSystemInputSchema>;

export const AccessReviewCountsSchema = z.object({
  total: z.number().int(),
  pending: z.number().int(),
  confirmed: z.number().int(),
  modified: z.number().int(),
  revoked: z.number().int(),
});
export type AccessReviewCounts = z.infer<typeof AccessReviewCountsSchema>;

export const AccessReviewSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  name: z.string(),
  collection: z.string(),
  /** NULL = every record of the collection. */
  recordId: z.string().nullable(),
  /** '(record deleted)' when the pointer no longer resolves. */
  recordName: z.string().nullable(),
  privilegedOnly: z.boolean(),
  status: AccessReviewStatusSchema,
  reviewerId: z.string(),
  reviewerName: z.string(),
  approverId: z.string().nullable(),
  approverName: z.string().nullable(),
  notes: z.string(),
  /** When the next review cycle is due (set on completion; default +3 months). */
  nextReviewOn: z.string().nullable(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  approvedAt: z.string().nullable(),
  counts: AccessReviewCountsSchema,
  systems: z.array(AccessReviewSystemSchema),
});
export type AccessReview = z.infer<typeof AccessReviewSchema>;

export const AccessReviewItemSchema = z.object({
  id: z.string(),
  reviewId: z.string(),
  assignmentId: z.string(),
  recordId: z.string(),
  /** Snapshot taken when the review started. */
  recordName: z.string(),
  recordDeleted: z.boolean(),
  userId: z.string(),
  userName: z.string(),
  userEmail: z.string(),
  accessLevel: AssignmentAccessLevelSchema,
  accessRole: z.string(),
  grantedOn: z.string().nullable(),
  approvedByName: z.string().nullable(),
  decision: AccessReviewDecisionSchema,
  newAccessLevel: AccessLevelSchema.nullable(),
  note: z.string(),
  decidedByName: z.string().nullable(),
  decidedAt: z.string().nullable(),
});
export type AccessReviewItem = z.infer<typeof AccessReviewItemSchema>;

export const CreateAccessReviewInputSchema = z.object({
  name: z.string().min(1).max(200),
  collection: AccessReviewCollectionSchema.default('software'),
  /** One record only; null/absent = the whole collection. */
  recordId: z.string().nullable().optional(),
  privilegedOnly: z.boolean().default(false),
  /** Defaults to the caller; must be an admin or reviewer member. */
  reviewerId: z.string().min(1).optional(),
  notes: z.string().max(5000).default(''),
});
export type CreateAccessReviewInput = z.infer<typeof CreateAccessReviewInputSchema>;

export const DecideAccessReviewItemInputSchema = z
  .object({
    decision: z.enum(['confirmed', 'modified', 'revoked', 'pending']),
    /** Required when the decision is 'modified'. */
    newAccessLevel: AccessLevelSchema.optional(),
    note: z.string().max(2000).default(''),
  })
  .refine((v) => v.decision !== 'modified' || v.newAccessLevel !== undefined, {
    message: 'Choose the new access level',
    path: ['newAccessLevel'],
  });
export type DecideAccessReviewItemInput = z.infer<typeof DecideAccessReviewItemInputSchema>;

export const CompleteAccessReviewInputSchema = z.object({
  notes: z.string().max(5000).optional(),
  /** Default = completion date + 3 months. */
  nextReviewOn: DateOnlySchema.optional(),
});
export type CompleteAccessReviewInput = z.infer<typeof CompleteAccessReviewInputSchema>;
